import React, { useState, useEffect } from 'react';
import { message, Spin, Space, Button, Select } from 'antd';
import axios from 'axios';
import GenericTable from '../components/GenericTable';
import GenericModal from '../components/GenericModal ';
import './Users.css';
import AppMenu from '../components/AppMenu';
import { format } from 'date-fns';

const { Option } = Select;

const Users = () => {
  const [usersData, setUsersData] = useState([]);
  const [filteredUsersData, setFilteredUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(''); // State for selected organization filter
  const [selectedUser, setSelectedUser] = useState(null); // To hold the selected user for password change
  const [isPasswordModal, setIsPasswordModal] = useState(false); // To differentiate between user creation and password change

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
  }, []);

  // Fetch users data
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/organizations/users`);
      const formattedUsers = formatUsersData(response.data);
      setUsersData(formattedUsers);
      setFilteredUsersData(formattedUsers); // Initially set filtered data to full data
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Failed to load users');
      setLoading(false);
    }
  };

  // Fetch list of organizations
  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/organizations`);
      setOrganizations(response.data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      message.error('Failed to load organizations');
    }
  };

  // Handle organization filter change
  const handleOrganizationChange = (value) => {
    setSelectedOrg(value);
    const filteredData = value
      ? usersData.filter((user) => user.organizationId === value)
      : usersData; // If no organization is selected, show all users
    setFilteredUsersData(filteredData);
  };

  // Handle user creation
  const handleCreateUser = async (formData) => {
    try {
      const { name, email, password, organizationId } = formData;
      await axios.post(
        `${process.env.REACT_APP_API}/api/auth/create-user`, 
        { name, email, password },
        { params: { organizationId } }
      );
      message.success('User created successfully');
      fetchUsers();
      setModalVisible(false);
    } catch (error) {
      console.error('Error creating user:', error);
      message.error('Failed to create user');
    }
  };

  const handleChangePassword = async (userId, newPassword) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/api/auth/change-password/${userId}`,
        { newPassword }
      );
  
      message.success('Password changed successfully for the user');
      setModalVisible(false); // Close the modal after successful change
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response && error.response.status === 401) {
        message.error('Unauthorized: You do not have permission to change this password');
      } else {
        message.error('Failed to change password. Please try again later.');
      }
    }
  };

  // Toggle user status
  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await axios.patch(
        `${process.env.REACT_APP_API}/api/organizations/users/${userId}/toggle-status`,
        {}
      );
      message.success(`User status changed to ${newStatus}`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      message.error('Failed to toggle user status');
    }
  };

  // Format data for table
  const formatUsersData = (data) => {
    return data.flatMap(org => 
      org.users.map(user => ({
        organizationId: org.organizationId,
        organizationName: org.organizationName,
        userId: user.userId,
        userName: user.userName,
        email: user.email,
        verified: user.verified ? 'Yes' : 'No',
        status: user.status,
        role: user.role,
      }))
    );
  };

  // Open the password change modal
  const openChangePasswordModal = (user) => {
    setSelectedUser(user);
    setIsPasswordModal(true);
    setModalVisible(true);
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />;
  }

  return (
    <div style={{ display: 'flex' }}>
      <AppMenu />
      <div className="users-page">
        <Space style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Users across Organizations</h2>

          {/* Organization Filter Dropdown */}
         

          <Button type="primary" onClick={() => { setIsPasswordModal(false); setModalVisible(true); }}>
            Create User
          </Button>
        </Space>


        <div style={{ display: "flex", justifyContent: "end" }}>
  <Select
    className="custom-select"  // Apply the custom CSS class
    placeholder="Filter by Organization"
    style={{ width: '300px', color: 'white' }} // Ensure the color for selected text is also white
    onChange={handleOrganizationChange}
    allowClear
    showSearch
    optionFilterProp="children"
    filterOption={(input, option) =>
      option.children.toLowerCase().includes(input.toLowerCase())
    }
  >
    {organizations.map((org) => (
      <Option key={org.id} value={org.id}>
        {org.name}
      </Option>
    ))}
  </Select>
</div>
       

        <GenericTable
          data={filteredUsersData} // Use filtered data
          searchKeys={['organizationName', 'userName', 'email']}
          sortableColumns={['userName', 'email', 'verified', 'status', 'role', 'createdAt', 'updatedAt']}
          actions={['Toggle Status', 'Change Password']}
          actionFunctions={[
            (record) => toggleUserStatus(record.userId, record.status),
            (record) => openChangePasswordModal(record),
          ]}
          filterableColumns={['organizationName']}
          toggle="status"
        />

        {/* Modal for both User Creation and Password Change */}
        <GenericModal
          isVisible={modalVisible}
          handleOk={(formData) => {
            if (isPasswordModal) {
              handleChangePassword(selectedUser.userId, formData.newPassword);
            } else {
              handleCreateUser(formData);
            }
          }}
          handleCancel={() => setModalVisible(false)}
          title={isPasswordModal ? 'Change User Password' : 'Create New User'}
          inputs={isPasswordModal ? [
            { name: 'newPassword', label: 'New Password', type: 'password', require: true },
          ] : [
            { name: 'name', label: 'Name', type: 'text', require: true },
            { name: 'email', label: 'Email', type: 'email', require: true },
            { name: 'password', label: 'Password', type: 'password', require: true },
            {
              label: 'Organization',
              name: 'organizationId',
              type: 'select',
              options: organizations?.map(org => ({ value: org.id, label: org.name })),
              placeholder: 'Select Organization',
              require: true,
            },
          ]}
          actions={['Submit', 'Cancel']}
          actionFunctions={[
            (formData) => {
              if (isPasswordModal) {
                handleChangePassword(selectedUser.userId, formData.newPassword);
              } else {
                handleCreateUser(formData);
              }
            },
            () => setModalVisible(false),
          ]}
        />
      </div>
    </div>
  );
};

export default Users;
